import { ENDPOINTS } from '@api/endpoints';
import api, { handleError } from '@/api';
import { NOTIFICATION_MAX_PAGE_SIZE } from '@/config/constants';
import type { UserNotification, UserNotificationRequest } from '@/types/notifications';

const {
  NOTIFICATIONS: { GET_ALL_USER_NOTIFICATIONS, MARK_NOTIFICATIONS_READ },
} = ENDPOINTS;

/**
 * Notification related API calls
 */
export const notificationService = {
  /**
   * Get all user notifications with pagination
   * @param params - Pagination parameters
   * @returns User notification details
   * @throws {{ code: string; message: string }} - If the API call fails
   */
  getAllUserNotifications: async (params: UserNotificationRequest, signal?: AbortSignal) => {
    try {
      const response = await api.get<typeof GET_ALL_USER_NOTIFICATIONS.responseType>(
        `${GET_ALL_USER_NOTIFICATIONS.path}`,
        {
          params: {
            pageNumber: params.pageNo,
            pageSize: params.pageSize,
            ...(params.status ? { status: params.status } : {}),
          },
          signal,
        }
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Fetches all unread notifications, paginating with the API max page size.
   */
  getAllUnreadUserNotifications: async (signal?: AbortSignal): Promise<UserNotification[]> => {
    const notifications: UserNotification[] = [];
    let pageNo = 1;
    let totalPages = 1;

    do {
      const response = await notificationService.getAllUserNotifications(
        {
          pageNo,
          pageSize: NOTIFICATION_MAX_PAGE_SIZE,
          status: 'unread',
        },
        signal,
      );

      notifications.push(...(response.data?.notifications ?? []));
      totalPages = response.data?.totalPages ?? 1;
      pageNo += 1;
    } while (pageNo <= totalPages);

    return notifications;
  },

  /**
   * Mark notifications as read for a user
   * @param data - Mark as read request data
   * @returns Success response
   * @throws {{ code: string; message: string }} - If the API call fails
   */
  markNotificationsAsRead: async (data: {
    notificationIds: Array<string | number>;
    groupId: number;
  }) => {
    try {
      const response = await api.put<typeof MARK_NOTIFICATIONS_READ.responseType>(
        MARK_NOTIFICATIONS_READ.path,
        data
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
};
